/**
 * Photo Project Service
 * API integration for photo editor projects
 */

import { authHeaders } from '../../shared/utils/authHelpers';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Debug logging (set to true to enable)
const DEBUG = false;

const logRequest = (method, url, headers, body) => {
  if (!DEBUG) return;
  console.group(`📤 [PhotoProjectService] ${method} ${url}`);
  console.log('Headers:', headers);
  if (body) console.log('Body:', body);
  console.groupEnd();
};

const logResponse = (method, url, status, data) => {
  if (!DEBUG) return;
  const icon = status >= 200 && status < 300 ? '✅' : '❌';
  console.group(`${icon} [PhotoProjectService] ${method} ${url} - ${status}`);
  console.log('Response:', data);
  console.groupEnd();
};

const getAuthHeaders = () => {
  const headers = {
    ...authHeaders(),
    'Content-Type': 'application/json'
  };
  return headers;
};

const PhotoProjectService = {
  /**
   * List all projects
   */
  async listProjects({ page = 1, limit = 20, search = '', sort = 'updatedAt', order = 'desc' } = {}) {
    const params = new URLSearchParams({ page, limit, sort, order });
    if (search) params.append('search', search);

    const url = `${API_BASE}/api/photo-projects?${params}`;
    const headers = getAuthHeaders();

    logRequest('GET', url, headers, null);

    const response = await fetch(url, { headers });
    const data = await response.json().catch(() => ({}));

    logResponse('GET', url, response.status, data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch projects');
    }

    return {
      projects: Array.isArray(data?.data) ? data.data : (data.projects || []),
      pagination: data.pagination || { page, limit, total: data?.data?.length || data?.projects?.length || 0, totalPages: 1 }
    };
  },

  /**
   * Get a single project by ID
   */
  async getProject(id) {
    const url = `${API_BASE}/api/photo-projects/${id}`;
    const headers = getAuthHeaders();

    logRequest('GET', url, headers, null);

    const response = await fetch(url, { headers });
    const data = await response.json().catch(() => ({}));

    logResponse('GET', url, response.status, data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch project');
    }

    return data?.data || data;
  },

  /**
   * Create a new project
   */
  async createProject({ name, templateId, width, height }) {
    const url = `${API_BASE}/api/photo-projects`;
    const headers = getAuthHeaders();
    const body = { name, templateId, width, height, elements: [], backgroundColor: '#ffffff' };

    logRequest('POST', url, headers, body);

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });

    const data = await response.json().catch(() => ({}));

    logResponse('POST', url, response.status, data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to create project');
    }

    return data?.data || data;
  },

  /**
   * Update a project
   */
  async updateProject(id, { name, elements, backgroundColor, thumbnail }) {
    const url = `${API_BASE}/api/photo-projects/${id}`;
    const headers = getAuthHeaders();
    const body = {};
    if (name !== undefined) body.name = name;
    if (elements !== undefined) body.elements = elements;
    if (backgroundColor !== undefined) body.backgroundColor = backgroundColor;
    if (thumbnail !== undefined) body.thumbnail = thumbnail;

    logRequest('PUT', url, headers, body);

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body)
    });

    const data = await response.json().catch(() => ({}));

    logResponse('PUT', url, response.status, data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to update project');
    }

    return data?.data || data;
  },

  /**
   * Delete a project
   */
  async deleteProject(id) {
    const url = `${API_BASE}/api/photo-projects/${id}`;
    const headers = getAuthHeaders();

    logRequest('DELETE', url, headers, null);

    const response = await fetch(url, {
      method: 'DELETE',
      headers
    });

    const data = await response.json().catch(() => ({}));

    logResponse('DELETE', url, response.status, data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to delete project');
    }

    return data?.data || data;
  },

  /**
   * Duplicate a project
   */
  async duplicateProject(id, name) {
    const url = `${API_BASE}/api/photo-projects/${id}/duplicate`;
    const headers = getAuthHeaders();
    const body = { name };

    logRequest('POST', url, headers, body);

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });

    const data = await response.json().catch(() => ({}));

    logResponse('POST', url, response.status, data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to duplicate project');
    }

    return data?.data || data;
  },

  /**
   * Upload an asset
   */
  async uploadAsset(file, projectId = null) {
    const url = `${API_BASE}/api/photo-assets/upload`;
    const headers = authHeaders(); // No Content-Type for FormData

    const formData = new FormData();
    formData.append('file', file);
    if (projectId) formData.append('projectId', projectId);

    logRequest('POST', url, headers, { file: file.name, projectId });

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData
    });

    const data = await response.json().catch(() => ({}));

    logResponse('POST', url, response.status, data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to upload asset');
    }

    return data;
  },

  /**
   * List user assets
   */
  async listAssets({ page = 1, limit = 50, projectId = null } = {}) {
    const params = new URLSearchParams({ page, limit });
    if (projectId) params.append('projectId', projectId);

    const url = `${API_BASE}/api/photo-assets?${params}`;
    const headers = getAuthHeaders();

    logRequest('GET', url, headers, null);

    const response = await fetch(url, { headers });
    const data = await response.json().catch(() => ({}));

    logResponse('GET', url, response.status, data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch assets');
    }

    return data;
  }
};

export default PhotoProjectService;
